/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import { Button, Text } from "@adobe/react-spectrum";
import { getEndpointName } from "@apollo/jsonforms-react-spectrum-renderers/src/util/commonUtils";
import {
  RankedTester,
  rankWith,
  uiTypeIs,
  RendererProps,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
import { get } from "lodash";
import React, { useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Utils
import {
  evaluateContextRule,
  extractPath,
  resolveContextValue,
} from "../utils/commonUtils";

import Loader from "../components/Loader/Loader";
import { AllIcons } from "../utils/icons";
import styles from "./css/ButtonRenderer.module.scss";

// import { imsActions } from "../components/IMS";
// import { ImsAuthContext } from "../context/IMSAuthContext";

declare const window: any;

export const buttonRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("Button")
);

// Exported for testing

export function ButtonRenderer({ uischema, visible, enabled }: RendererProps) {
  const type = uischema.options?.redirect?.type;
  let path = uischema.options?.redirect?.path;
  let label = uischema.options?.label;
  const spectrumProps = uischema.options?.spectrumProps;
  const style = uischema.options?.style;
  const newTab = uischema.options?.newTab;
  const buttonId = uischema.options?.buttonId;
  const endpointPath = uischema.options?.endpointPath;
  const actionType = uischema.options?.redirect?.actionType;
  const redirectPath = uischema.options?.redirect?.redirectPath;
  const loader = uischema.options?.loader || false;
  const loaderStyle = uischema.options?.loaderStyle || {};
  let iconJsx = null;
  const iconName = uischema.options?.icon?.name;
  const iconSpectrumProps = uischema.options?.icon?.spectrumProps;

  const context = useJsonForms();
  const buttonCallback =
    context?.config?.lifecycleHooks?.onSuccessCallbacks?.button;

  const handleButtonStyle = buttonCallback?.handleButtonStyle;
  const handleButtonLoader = buttonCallback?.handleButtonLoader;
  const onHandleClick = buttonCallback?.onHandleClick;
  const [buttonStyle, setButtonStyle] = useState(style);
  const [showLoader, setShowLoader] = useState(loader);
  const navigate = useNavigate();
  if (endpointPath) {
    const pathKey = getEndpointName(endpointPath, context?.config);
    const endpoint = get(context?.config, pathKey, "");
    path = endpoint + path;
  }
  if (handleButtonStyle) {
    handleButtonStyle({
      type,
      buttonStyle,
      setButtonStyle,
      buttonId,
    });
  }
  if (handleButtonLoader) {
    handleButtonLoader({
      showLoader,
      setShowLoader,
      buttonId,
    });
  }
  function handleClick() {
    if (onHandleClick) {
      onHandleClick({ isClicked: true, actionType });
    }
  }

  let onPressHandler;

  switch (type) {
    case "route":
      onPressHandler = () => {
        handleClick();
        navigate(path);
      };
      break;

    case "external":
      onPressHandler = () => {
        handleClick();
        if (newTab) {
          window.open(path, "_blank");
        } else {
          window.open(path, "_self");
        }
      };

      break;
    case "IMSLogin":
    case "IMSRegister":
      onPressHandler = () => {
        handleClick();
        // window.adobeIMS.setRedirectURI(window.location.origin + path);
        if (redirectPath) {
          try {
            localStorage.setItem("RedirectPath", redirectPath);
          } catch (error) {
            /* empty */
          }
        }
        if (type === "IMSRegister") {
          window.adobeIMS.signUp();
        } else {
          window.adobeIMS.signIn();
        }
      };
      break;
    case "onClick":
      onPressHandler = handleClick;
      break;
    default:
      break;
  }

  if (Object.keys(AllIcons).includes(iconName)) {
    const Icon = AllIcons[iconName];
    iconJsx = (
      <Suspense fallback={<div>Loading...</div>}>
        <Icon {...iconSpectrumProps} />
      </Suspense>
    );
  }

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  const reduxStateObj = useSelector((state: any) => state?.reduxState);
  const contextLabel = resolveContextValue(reduxStateObj, label);
  if (typeof contextLabel !== "undefined") {
    label = contextLabel;
  }

  return (
    <Button
      aria-label={label || iconName}
      isDisabled={!componentEnabled}
      {...spectrumProps}
      UNSAFE_className={!label && iconName && styles.buttonStyleIconOnly}
      UNSAFE_style={{ ...buttonStyle, cursor: "pointer" }}
      onPress={onPressHandler}
      isHidden={!componentVisibility}
    >
      {iconJsx}
      {label && <Text>{label}</Text>}
      {showLoader && <Loader size="S" style={loaderStyle} />}
    </Button>
  );
}

export default withJsonFormsLayoutProps(ButtonRenderer);
